import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';

// RxJS
import { filter, map, Observable, switchMap, take } from 'rxjs';

// Services
import { ProjectService } from '../services/project.service';

// Types
import { Project } from '../types/project';

/**
 * Resolves the `:projectId` route segment. The URL is the source of truth for the selected
 * project — this guard pushes it into ProjectService, never the other way round.
 */
@Injectable({
  providedIn: 'root'
})
export class ProjectGuard implements CanActivate {

  // Top-level pages that can appear where a project id is expected, i.e. old bookmarks such as
  // `/users` saved before project ids were part of the URL.
  private pages = ['records', 'users', 'project', 'settings', 'account'];

  /**
   * Constructor
   */

  constructor(
    private projectService: ProjectService,
    private router: Router,
  ) {}

  /**
   * Methods
   */

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    return this.projectService.getProjectsLoaded()
      .pipe(
        filter(loaded => loaded),
        take(1),
        switchMap(() => this.projectService.getProjects().pipe(take(1))),
        map(projects => {
          const projectId = route.paramMap.get('projectId');
          const project = projects.find(item => item.id === projectId);
          if (project?.id) {
            this.projectService.setCurrentProject(project.id);
            return true;
          }
          return this.redirectToDefault(projects, projectId);
        })
      );
  }

  /**
   * Helpers
   */

  private redirectToDefault(projects: Project[], segment?: string | null): boolean | UrlTree {
    const fallback = projects.find(project => project.id === this.projectService.currentProject)
      || projects[0];
    // Nothing to redirect to — let the project-less route render its empty state.
    if (!fallback?.id) { return true; }
    const page = segment && this.pages.includes(segment) ? segment : 'records';
    return this.router.createUrlTree(['/', fallback.id, page]);
  }

}
